import { defaultThemeV2 } from "../../theme/defaultThemeV2";
import type { QuestionCore } from "../contracts";

export type VideoRaw = {
  type: "video";
  style: "vdoCipher" | "vdoCipher_dial_bottom" | "vdoCipher_dial_right";
  content: string;
  start_message: string;
  video: string;
  number_seconds: number;
  options?: { value: number; text: string; display_text: boolean }[];
  playing_message?: string;
  post_data?: string;
  default?: number;
  auto_response?: string | null;
  start_position_ms?: number;
  end_position_ms?: number;
};

export type VideoDefinition = {
  prompt: string;
  startMessage: string;
  videoUrl: string;
  style: VideoRaw["style"];
  postData: string;
  startPositionSeconds: number;
  endPositionSeconds?: number;
  dial?: {
    position: "bottom" | "right";
    points: readonly { value: number; label: string }[];
    caption: string;
    initialValue: number;
  };
};

export type VideoDraft = {
  ended: boolean;
  completionReady: boolean;
  completionFailed: boolean;
  dialValue: number | null;
};
export type VideoEvent =
  | { type: "end" }
  | { type: "completion-ready" }
  | { type: "completion-failed" }
  | { type: "retry" }
  | { type: "set-dial"; value: number };
export type VideoResponse = { type: "video"; value: "Video viewed" };

export const videoCore: QuestionCore<
  VideoRaw,
  VideoDefinition,
  VideoDraft,
  VideoEvent,
  VideoResponse
> = {
  decode: (raw) => {
    if (typeof raw.video !== "string" || !raw.video) {
      throw new Error("Video question requires a playback URL.");
    }
    const position =
      raw.style === "vdoCipher_dial_bottom"
        ? "bottom"
        : raw.style === "vdoCipher_dial_right"
          ? "right"
          : undefined;
    if (raw.style !== "vdoCipher" && !position) {
      throw new Error("Unsupported video style.");
    }
    if (!raw.post_data) {
      throw new Error("Video question requires completion data.");
    }
    const startPositionMs = raw.start_position_ms ?? 0;
    if (!Number.isFinite(startPositionMs) || startPositionMs < 0) {
      throw new Error("Video start position must be a non-negative number.");
    }
    if (
      raw.end_position_ms !== undefined &&
      (!Number.isFinite(raw.end_position_ms) ||
        raw.end_position_ms <= startPositionMs)
    ) {
      throw new Error("Video end position must be after the start position.");
    }
    if (
      position &&
      (!Array.isArray(raw.options) ||
        raw.options.length < 2 ||
        raw.options.some((point) => !Number.isInteger(point.value)) ||
        new Set(raw.options.map((point) => point.value)).size !==
          raw.options.length ||
        !raw.post_data ||
        !Number.isInteger(raw.default) ||
        raw.default! < Math.min(...raw.options.map((point) => point.value)) ||
        raw.default! > Math.max(...raw.options.map((point) => point.value)))
    ) {
      throw new Error(
        "Video dial requires ordered points, a start value and sample data.",
      );
    }
    return {
      prompt: raw.content,
      startMessage: raw.start_message,
      videoUrl: raw.video,
      style: raw.style,
      postData: raw.post_data,
      startPositionSeconds: startPositionMs / 1000,
      ...(raw.end_position_ms === undefined
        ? {}
        : { endPositionSeconds: raw.end_position_ms / 1000 }),
      ...(position
        ? {
            dial: {
              position,
              points: raw.options!.map((point) => ({
                value: point.value,
                label: point.text,
              })),
              caption: raw.playing_message ?? "",
              initialValue: raw.default!,
            },
          }
        : {}),
    };
  },
  initialize: (definition) => ({
    ended: false,
    completionReady: false,
    completionFailed: false,
    dialValue: definition.dial?.initialValue ?? null,
  }),
  update: (definition, draft, event) => {
    if (event.type === "end")
      return { ...draft, ended: true, completionFailed: false };
    if (event.type === "completion-ready")
      return { ...draft, completionReady: true, completionFailed: false };
    if (event.type === "completion-failed")
      return { ...draft, completionFailed: true };
    if (event.type === "retry")
      return {
        ...draft,
        ended: false,
        completionReady: false,
        completionFailed: false,
      };
    const dial = definition.dial;
    if (
      !dial ||
      !Number.isInteger(event.value) ||
      event.value < Math.min(...dial.points.map((point) => point.value)) ||
      event.value > Math.max(...dial.points.map((point) => point.value))
    )
      return draft;
    return { ...draft, dialValue: event.value };
  },
  validate: (_definition, draft, copy = defaultThemeV2.copy) =>
    draft.ended && draft.completionReady
      ? { valid: true }
      : {
          valid: false,
          error: draft.ended
            ? copy.validation.videoSaving
            : copy.validation.videoWatch,
        },
  encode: (definition, draft) => {
    const validation = videoCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    return { type: "video", value: "Video viewed" };
  },
  projectTranscript: (definition, draft, copy = defaultThemeV2.copy) => {
    const validation = videoCore.validate(definition, draft, copy);
    if (!validation.valid) throw new Error(validation.error);
    return {
      kind: "video",
      question: definition.prompt,
      answer: copy.status.videoViewed,
    };
  },
};
